import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatStepper } from '@angular/material/stepper';
import { Project, PROJECT_URGENCY_OBJECT } from '@data/models/project.model';
import { Period, ServiceLevel } from '@data/models/sla-period.model';
import { InventoryService } from '@data/services/inventory.service';
import { chunk } from 'lodash';
import { lastValueFrom } from 'rxjs';
import { Inventory, Operation } from 'src/app/data/models/inventory.model';

interface Metadata {
    key: string;
    value: string;
}

const SOLVE_INVENTORY_CHUNK_SIZE = 100;

@Component({
    selector: 'syn-inventory-bulk-edit',
    templateUrl: './inventory-bulk-edit.component.html',
    styleUrls: ['./inventory-bulk-edit.component.scss']
})
export class InventoryBulkEditComponent implements OnInit {
    @Input() inventoryIds: string[] = [];
    @Input() project: Project;
    @Input() periods: Period[];

    @Output() close = new EventEmitter();
    @Output() updated = new EventEmitter();
    @ViewChild('stepper') stepper: MatStepper;

    loading = false;
    bulkEditForm: FormGroup;
    tagForm: FormGroup;
    addMetadataForm: FormGroup;
    removeMetadataForm: FormGroup;

    possibleStatus: string[] = [
        'RELEASED', 'UNPROVISIONED', 'SUPPLIED', 'STORED', 'UNPROVISIONING', 'BUILDING'
    ];
    possibleServiceTypes: string[] = [
        'BASIC',
        'BRONZE',
        'GOLD',
        'MANAGED_APPS',
        'MANAGED_BY_CLOUD_PROVIDER',
        'MANAGED_DATABASE',
        'MANAGED_IAAS',
        'MANAGED_HOSTING',
        'MANAGED_MIDDLEWARE',
        'MANAGED_OS',
        'SHARED',
        'SILVER',
        'STANDARD',
        'UNKNOWN',
        'NOT_MANAGED'
    ];

    tagsToAdd: string[] = [];
    tagsToRemove: string[] = [];
    metadatasToAdd: Metadata[] = [];
    metadatasToRemove: string[] = [];

    inventories: Inventory[] = [];
    updatedInventories: Inventory[] = [];
    untouchedInventories: Inventory[] = [];
    errors: { inventory: Inventory; error: string }[] = [];

    inventoryUpdatedChartData: { name: string; value: number }[] | undefined = undefined;
    inventoryUpdatedChartScheme = {
        domain: [
            getComputedStyle(document.documentElement).getPropertyValue('--clara-alternative--background'),
            getComputedStyle(document.documentElement).getPropertyValue('--clara-alternative'),
            getComputedStyle(document.documentElement).getPropertyValue('--clara-red')
        ]
    };

    constructor(
        private readonly fb: FormBuilder,
        private readonly inventoryService: InventoryService
    ) { }

    get statusEnabled() {
        return this.bulkEditForm.get('statusEnabled')?.value;
    }

    get hasUpdateParameters(): boolean {
        const { statusEnabled, serviceTypeEnabled, serviceLevelEnabled, tagsEnabled, metadataEnabled, status, serviceType } = this.bulkEditForm.value;

        return (statusEnabled && status) || (serviceTypeEnabled && serviceType) || serviceLevelEnabled
            || (tagsEnabled && (this.tagsToAdd.length > 0 || this.tagsToRemove.length > 0))
            || (metadataEnabled && (this.metadatasToAdd.length > 0 || this.metadatasToRemove.length > 0));
    }

    async ngOnInit(): Promise<void> {
        this.bulkEditForm = this.fb.group({
            statusEnabled: [false],
            serviceTypeEnabled: [false],
            serviceLevelEnabled: [false],
            tagsEnabled: [false],
            metadataEnabled: [false],

            status: [''],
            serviceType: [''],
            serviceLevel: [''],
            tags: [[]],
        });

        if (this.project.serviceLevels.length === 0) {
            this.bulkEditForm.get('serviceLevelEnabled').disable();
        }

        this.tagForm = this.fb.group({
            addTag: [''],
            removeTag: ['']
        });

        this.addMetadataForm = this.fb.group({
            key: ['', Validators.required],
            value: ['', Validators.required]
        });

        this.removeMetadataForm = this.fb.group({
            key: ['', Validators.required]
        });

        await this.solveInventoryIds();
    }

    inventoryPluralisation(inventoryCount: number) {
        return `${inventoryCount} ${inventoryCount > 1 ? 'inventaires' : 'inventaire'}`;
    }

    addNewTag() {
        const control = this.tagForm.get('addTag');
        this.tagsToAdd = this.addTag(control, this.tagsToAdd);
    }

    addRemoveTag() {
        const control = this.tagForm.get('removeTag');
        this.tagsToRemove = this.addTag(control, this.tagsToRemove);
    }

    removeAddTag(tag: string) {
        this.tagsToAdd = this.tagsToAdd.filter(t => t !== tag);
    }

    removeRemoveTag(tag: string) {
        this.tagsToRemove = this.tagsToRemove.filter(t => t !== tag);
    }

    addMetadata() {
        if (!this.addMetadataForm.valid) {
            return;
        }

        const { key, value } = this.addMetadataForm.value;
        if (!key?.trim()) {
            return;
        }
        const existingIndex = this.metadatasToAdd.findIndex(m => m.key === key);

        if (existingIndex >= 0) {
            this.metadatasToAdd[existingIndex].value = value;
        } else {
            this.metadatasToAdd.push({ key, value });
        }

        this.addMetadataForm.reset();
    }

    removeMetadata() {
        if (!this.removeMetadataForm.valid) {
            return;
        }

        const { key } = this.removeMetadataForm.value;
        if (!key?.trim()) {
            return;
        }

        if (!this.metadatasToRemove.includes(key)) {
            this.metadatasToRemove.push(key);
        }

        this.removeMetadataForm.reset();
    }

    removeAddMetadata(key: string) {
        this.metadatasToAdd = this.metadatasToAdd.filter(m => m.key !== key);
    }

    removeRemoveMetadata(key: string) {
        this.metadatasToRemove = this.metadatasToRemove.filter(k => k !== key);
    }

    submit() {
        this.loading = true;
        this.stepper.next();

        this.inventoryService.bulkEditInventory(
            this.inventoryIds,
            this.getOperations()
        ).subscribe(({ updatedInventoryIds, errors }) => {
            this.loading = false;
            this.updatedInventories = [];
            this.untouchedInventories = [];
            this.errors = [];

            for (const inventory of this.inventories) {
                const updatedInventory = updatedInventoryIds.find(id => id === inventory.id);

                if (updatedInventory) {
                    this.updatedInventories.push(inventory);
                    continue;
                }

                if (errors[inventory.id]) {
                    this.errors.push({ inventory, error: errors[inventory.id] });
                    continue;
                }

                this.untouchedInventories.push(inventory);
            }

            this.inventoryUpdatedChartData = [
                { name: 'Inventaires mis à jour', value: Math.round(this.updatedInventories.length * 100 / this.inventories.length) },
                { name: 'Inventaires inchangés', value: Math.round(this.untouchedInventories.length * 100 / this.inventories.length) },
                { name: 'Inventaires en erreur', value: Math.round(this.errors.length * 100 / this.inventories.length) }
            ];

            this.updated.emit();
        });
    }

    closePanel() {
        this.close.emit();
    }

    displayServiceLevel(selectedId: string): string {
        const serviceLevel = this.project.serviceLevels.find(up => up.id === selectedId);
        if (!serviceLevel) {
            return 'Non spécifié';
        }

        return `${this.getUrgencyName(serviceLevel.urgency)} - ${this.getPeriodName(serviceLevel.period)}`;
    }

    getProjectServiceLevel(serviceLevelId: string): ServiceLevel {
        return this.project.serviceLevels.find(serviceLevel => serviceLevel.id === serviceLevelId);
    }

    getUrgencyName(urgency: string): string {
        return PROJECT_URGENCY_OBJECT[urgency]?.translate;
    }

    getPeriodName(periodId: string): string {
        return this.periods.find(p => p.id === periodId)?.name || 'Inconnu';
    }

    private addTag(control: AbstractControl, tags: string[]): string[] {
        const tag = (control.value || '').trim();
        if (!tag?.length) {
            return tags;
        }

        const updatedTags = new Set(tags);
        updatedTags.add(tag);
        control.reset();
        return Array.from(updatedTags);
    }

    private getOperations(): Operation[] {
        const { statusEnabled, serviceTypeEnabled, serviceLevelEnabled, tagsEnabled, metadataEnabled, status, serviceType, serviceLevel } = this.bulkEditForm.value;
        const operations: Operation[] = [];

        if (statusEnabled && status) {
            operations.push({
                op: 'REPLACE',
                path: 'status',
                value: status,
            });
        }

        if (serviceTypeEnabled && serviceType) {
            operations.push({
                op: 'REPLACE',
                path: 'attributes.serviceType',
                value: serviceType,
            });
        }

        if (serviceLevelEnabled) {
            operations.push({
                op: 'REPLACE',
                path: 'serviceLevel',
                value: serviceLevel,
            });
        }

        if (tagsEnabled) {
            if (this.tagsToAdd.length > 0) {
                operations.push({
                    op: 'ADD',
                    path: 'tags',
                    value: this.tagsToAdd
                });
            }

            if (this.tagsToRemove.length > 0) {
                operations.push({
                    op: 'REMOVE',
                    path: 'tags',
                    value: this.tagsToRemove
                });
            }
        }

        if (metadataEnabled) {
            if (this.metadatasToAdd.length > 0) {
                operations.push({
                    op: 'ADD',
                    path: 'metadata',
                    value: this.metadatasToAdd.reduce((previous, current) => ({
                        ...previous,
                        [current.key]: current.value
                    }), {})
                });
            }

            if (this.metadatasToRemove.length > 0) {
                operations.push({
                    op: 'REMOVE',
                    path: 'metadata',
                    value: this.metadatasToRemove
                });
            }
        }

        return operations;
    }

    private async solveInventoryIds(): Promise<void> {
        this.loading = true;
        const chunks = chunk(this.inventoryIds, SOLVE_INVENTORY_CHUNK_SIZE);

        for (const c of chunks) {
            const { embedInventories } = await lastValueFrom(this.inventoryService.findInventory({
                filter: `id:(${c.join('|')})`,
                fields: 'id,name',
                page: 1,
                limit: SOLVE_INVENTORY_CHUNK_SIZE,
            }));

            this.inventories.push(...embedInventories);
        }

        this.loading = false;
    }
}
